// On-device encrypted storage for personal data (AES-GCM, non-extractable key kept in IndexedDB).
// Sync getItem/setItem API backed by an in-memory cache that is decrypted once at boot.

export const SENSITIVE_KEYS = [
  'zi_will_draft_v1',
  'zi_will_prefill_v1',
  'zi_will_annex_v1',
  'zi-inheritance-state-v1',
  'zi_inheritance_snapshot_v1',
  'zi_zakat_snapshot_v1',
  'zakatCalculations',
  'zi-life-eol-v1',
  'zi-life-waqfplan-v1',
  'zi-life-budget-v1',
  'zi-life-sadaqah-v1',
  'amanah_prayer_coords_v1',
  'amanah_plan',
];

const PREFIX = 'enc1:';
const DB_NAME = 'amanah-vault';
const STORE = 'keys';
const KEY_ID = 'device-key-v1';
const SENSITIVE = new Set(SENSITIVE_KEYS);

const cache = new Map();
const writeSeq = new Map();
let deviceKey = null;

const ls = () => (typeof window !== 'undefined' ? window.localStorage : null);

const b64 = {
  // Chunked to avoid "Maximum call stack / too many arguments" RangeError on values > ~125 KB.
  enc: (buf) => {
    const bytes = new Uint8Array(buf);
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
      bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    }
    return btoa(bin);
  },
  dec: (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0)),
};

function idbRequest(mode, fn) {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, 1);
    open.onupgradeneeded = () => open.result.createObjectStore(STORE);
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const tx = open.result.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => open.result.close();
    };
  });
}

async function loadOrCreateKey() {
  const existing = await idbRequest('readonly', (s) => s.get(KEY_ID));
  if (existing) return existing;
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  await idbRequest('readwrite', (s) => s.put(key, KEY_ID));
  return key;
}

async function encrypt(text) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, deviceKey, new TextEncoder().encode(text));
  return `${PREFIX}${b64.enc(iv)}.${b64.enc(ct)}`;
}

async function decrypt(stored) {
  const [iv, ct] = stored.slice(PREFIX.length).split('.');
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: b64.dec(iv) }, deviceKey, b64.dec(ct));
  return new TextDecoder().decode(pt);
}

async function persist(key, value) {
  const seq = (writeSeq.get(key) || 0) + 1;
  writeSeq.set(key, seq);
  const cipher = await encrypt(value);
  if (writeSeq.get(key) === seq && cache.get(key) === value) ls().setItem(key, cipher);
}

async function hydrateKey(key) {
  const raw = ls().getItem(key);
  if (raw == null) return;
  if (!raw.startsWith(PREFIX)) {
    cache.set(key, raw);
    await persist(key, raw);
    return;
  }
  try {
    cache.set(key, await decrypt(raw));
  } catch (e) {
    // Keep the ciphertext (never destroy user data); it is replaced on the next save.
    if (process.env.NODE_ENV !== 'production') console.warn('[secureStorage] could not decrypt', key, e);
  }
}

export async function initSecureStorage() {
  try {
    if (!ls() || !window.crypto?.subtle || !window.indexedDB) return;
    deviceKey = await loadOrCreateKey();
    await Promise.all(SENSITIVE_KEYS.map(hydrateKey));
  } catch (_e) {
    deviceKey = null;
    cache.clear();
  }
}

const isVaulted = (key) => deviceKey && SENSITIVE.has(key);

export const secureStorage = {
  getItem(key) {
    if (!isVaulted(key)) return ls()?.getItem(key) ?? null;
    return cache.has(key) ? cache.get(key) : null;
  },
  setItem(key, value) {
    if (!isVaulted(key)) { ls()?.setItem(key, value); return; }
    const text = String(value);
    cache.set(key, text);
    persist(key, text).catch((e) => {
      if (process.env.NODE_ENV !== 'production') console.warn('[secureStorage] write failed', key, e);
    });
  },
  removeItem(key) {
    cache.delete(key);
    writeSeq.set(key, (writeSeq.get(key) || 0) + 1);
    ls()?.removeItem(key);
  },
};

export const isStorageEncrypted = () => !!deviceKey;
